import { RootState } from '@/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
import { State } from './state';
// import ApiService from "@/services/ApiService";
import { ApiService } from '@/services/ApiService';

import { ActionTypes as CommonActionTypes } from '../Common/actions';
import { TeilnehmerAntwort } from '@/models/TeilnehmerAntwortModels';
import { ErrorResponseData } from '@/models/CommonModels';
import { BewertungAntwort, UmfrageInfo, GetUmfrageInfoRequest } from '@/models/UmfrageModels';

export enum ActionTypes {
  //#region ThemenUmfrage
  GetTeilnehmerantwort = 'GETTEILNEHMERANTWORT',
  GetTeilnehmerantwortByTeilnehmerguid = 'GETTEILNEHMERANTWORTBYTEILNEHMERGUID',
  GetTeilnehmerantwortByUmfrageguid = 'GETTEILNEHMERANTWORTBYUMFRAGEGUID',
  CreateTeilnehmerantwort = 'CREATETEILNEHMERANTWORT',
  CreateTeilnehmerantwortRange = 'CREATETEILNEHMERANTWORTRANGE',
  UpdateTeilnehmerantwort = 'UPDATETEILNEHMERANTWORT',
  UpdateTeilnehmerantwortRange = 'UPDATETEILNEHMERANTWORTRANGE',
  DeleteTeilnehmerantwort = 'DELETETEILNEHMERANTWORT',
  ClearTeilnehmerantwort = 'CLEARTEILNEHMERANTWORT',
  RemoveTeilnehmerantwortenByTeilnehmerguid = 'REMOVETEILNEHMERANTWORTENBYTEILNEHMERGUID',
  //#endregion

  //#region BewertungUmfrage
  GetAllBewertungAntwortByTeilnehmerguid = 'GETALLBEWERTUNGANTWORTBYTEILNEHMERGUID',
  GetAllBewertungAntwortByUmfrageguid = 'GETALLBEWERTUNGANTWORTBYUMFRAGEGUID',
  ClearBewertungAntworten = 'CLEARBEWERTUNGANTWORTEN',
  GetUmfrageInfo = 'GETUMFRAGEINFO',
  UpdateBewertungAntwort = 'UPDATEBEWERTUNGANTWORT',
  UpdateBewertungAntwortRange = 'UPDATEBEWERTUNGANTWORTRANGE',
  //#endregion
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  //#region ThemenUmfrage
  [ActionTypes.GetTeilnehmerantwort](context: ActionArguments): Promise<void>;
  [ActionTypes.GetTeilnehmerantwortByTeilnehmerguid](context: ActionArguments, guid: string): Promise<void>;
  [ActionTypes.GetTeilnehmerantwortByUmfrageguid](context: ActionArguments, guid: string): Promise<void>;
  [ActionTypes.CreateTeilnehmerantwort](context: ActionArguments, data: TeilnehmerAntwort): Promise<void>;
  [ActionTypes.CreateTeilnehmerantwortRange](context: ActionArguments, data: Array<TeilnehmerAntwort>): Promise<void>;
  [ActionTypes.UpdateTeilnehmerantwort](context: ActionArguments, data: TeilnehmerAntwort): Promise<void>;
  [ActionTypes.UpdateTeilnehmerantwortRange](context: ActionArguments, data: Array<TeilnehmerAntwort>): Promise<void>;
  [ActionTypes.DeleteTeilnehmerantwort](context: ActionArguments, teilnehmerantwortid: number): Promise<void>;
  [ActionTypes.ClearTeilnehmerantwort](context: ActionArguments): void;
  [ActionTypes.RemoveTeilnehmerantwortenByTeilnehmerguid](context: ActionArguments, teilnehmerguid: string): void;
  //#endregion

  //#region BewertungUmfrage
  [ActionTypes.GetAllBewertungAntwortByTeilnehmerguid](context: ActionArguments, guid: string): void;
  [ActionTypes.GetAllBewertungAntwortByUmfrageguid](context: ActionArguments, guid: string): void;
  [ActionTypes.ClearBewertungAntworten](context: ActionArguments): void;
  [ActionTypes.GetUmfrageInfo](context: ActionArguments, data: GetUmfrageInfoRequest): void;
  [ActionTypes.UpdateBewertungAntwort](context: ActionArguments, data: BewertungAntwort): Promise<void>;
  [ActionTypes.UpdateBewertungAntwortRange](context: ActionArguments, data: Array<BewertungAntwort>): Promise<void>;
  //#endregion
};

export const actions: ActionTree<State, RootState> & Actions = {
  //#region ThemenUmfrage
  [ActionTypes.ClearTeilnehmerantwort]({ commit }: ActionArguments) {
    commit(MutationTypes.SetTeilnehmerantworten, new Array<TeilnehmerAntwort>());
  },

  async [ActionTypes.GetTeilnehmerantwort]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getAllTeilnehmerAntwort()
      .then((res) => {
        const data: Array<TeilnehmerAntwort> = res.data;
        commit(MutationTypes.SetTeilnehmerantworten, data);
      })
      .catch((error) => {
        status.teilnehmerAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.getTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetTeilnehmerantwortByTeilnehmerguid]({ commit, dispatch, rootGetters }: ActionArguments, guid: string) {
    const status = rootGetters.status;
    status.getTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getAllTeilnehmerAntwortByTeilnehmerguid(guid)
      .then((res) => {
        const data: Array<TeilnehmerAntwort> = res.data;
        commit(MutationTypes.UpdateOrAddTeilnehmerantwortenRange, data);
      })
      .catch((error) => {
        status.teilnehmerAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.getTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetTeilnehmerantwortByUmfrageguid]({ commit, dispatch, rootGetters }: ActionArguments, guid: string) {
    const status = rootGetters.status;
    status.getTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.GetAllTeilnehmerAntwortByUmfrageguid(guid)
      .then((res) => {
        const data: Array<TeilnehmerAntwort> = res.data;
        commit(MutationTypes.SetTeilnehmerantworten, data);
      })
      .catch((error) => {
        status.teilnehmerAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.getTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  // GetAllTeilnehmerantwortByUmfrageguid

  async [ActionTypes.CreateTeilnehmerantwort]({ commit, dispatch, rootGetters }: ActionArguments, data: TeilnehmerAntwort) {
    const status = rootGetters.status;
    status.createTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.CreateTeilnehmerAntwort(data)
      .then((res) => {
        const data: TeilnehmerAntwort = res.data;
        commit(MutationTypes.AddTeilnehmerantworten, data);
      })
      .catch((error) => {
        status.teilnehmerAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.createTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateTeilnehmerantwortRange]({ commit, dispatch, rootGetters }: ActionArguments, data: Array<TeilnehmerAntwort>) {
    const status = rootGetters.status;
    status.createTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.CreateTeilnehmerAntwortRange(data)
      .then((res) => {
        const data: Array<TeilnehmerAntwort> = res.data;
        commit(MutationTypes.UpdateOrAddTeilnehmerantwortenRange, data);
      })
      .catch((error) => {
        if (error.response.status === 400) {
          const err = error.response.data as ErrorResponseData;
          status.teilnehmerAntwortErrorMsg = err.title;
        } else {
          status.teilnehmerAntwortErrorMsg = error.response;
        }
      })
      .finally(() => {
        status.createTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateTeilnehmerantwort]({ commit, dispatch, rootGetters }: ActionArguments, data: TeilnehmerAntwort) {
    const status = rootGetters.status;
    status.updateTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.UpdateTeilnehmerAntwort(data)
      .then((res) => {
        const data: TeilnehmerAntwort = res.data;
        commit(MutationTypes.UpdateTeilnehmerantworten, data);
      })
      .catch((error) => {
        status.teilnehmerAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.updateTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateTeilnehmerantwortRange]({ commit, dispatch, rootGetters }: ActionArguments, data: Array<TeilnehmerAntwort>) {
    const status = rootGetters.status;
    status.updateTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.UpdateTeilnehmerAntwortRange(data)
      .then((res) => {
        const data: Array<TeilnehmerAntwort> = res.data;
        commit(MutationTypes.UpdateOrAddTeilnehmerantwortenRange, data);
      })
      .catch((error) => {
        if (error.response.status === 400) {
          const err = error.response.data as ErrorResponseData;
          status.teilnehmerAntwortErrorMsg = err.title;
        } else {
          status.teilnehmerAntwortErrorMsg = error.response;
        }
      })
      .finally(() => {
        status.updateTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteTeilnehmerantwort]({ commit, dispatch, rootGetters }: ActionArguments, teilnehmerantwortid: number) {
    const status = rootGetters.status;
    status.deleteTeilnehmerAntwortLoading = true;
    status.teilnehmerAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.DeleteTeilnehmerAntwort(teilnehmerantwortid)
      .then((res) => {
        commit(MutationTypes.DeleteTeilnehmerantworten, teilnehmerantwortid);
      })
      .catch((error) => {
        status.teilnehmerAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.deleteTeilnehmerAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  [ActionTypes.RemoveTeilnehmerantwortenByTeilnehmerguid]({ commit }: ActionArguments, teilnehmerguid: string) {
    commit(MutationTypes.RemoveTeilnehmerantwortenByTeilnehmerguid, teilnehmerguid);
  },
  //#endregion

  //#region BewertungUmfrage
  async [ActionTypes.GetAllBewertungAntwortByTeilnehmerguid]({ commit, dispatch, rootGetters }: ActionArguments, guid: string) {
    const status = rootGetters.status;
    status.getBewertungAntwortLoading = true;
    status.bewertungAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.GetAllBewertungAntwortByTeilnehmerguid(guid)
      .then((res) => {
        const data: Array<BewertungAntwort> = res.data;
        commit(MutationTypes.SetBewertungantworten, data);
      })
      .catch((error) => {
        const err = error.response.data as ErrorResponseData;
        status.bewertungAntwortErrorMsg = err.title;
      })
      .finally(() => {
        status.getBewertungAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetAllBewertungAntwortByUmfrageguid]({ commit, dispatch, rootGetters }: ActionArguments, guid: string) {
    const status = rootGetters.status;
    status.getBewertungAntwortLoading = true;
    status.bewertungAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.GetAllBewertungAntwortByUmfrageguid(guid)
      .then((res) => {
        const data: Array<BewertungAntwort> = res.data;
        commit(MutationTypes.SetBewertungantworten, data);
      })
      .catch((error) => {
        const err = error.response.data as ErrorResponseData;
        console.log(err.title);
        status.bewertungAntwortErrorMsg = err.title;
      })
      .finally(() => {
        status.getBewertungAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  [ActionTypes.ClearBewertungAntworten]({ commit }: ActionArguments) {
    commit(MutationTypes.SetBewertungantworten, new Array<BewertungAntwort>());
  },
  //#endregion

  async [ActionTypes.GetUmfrageInfo]({ commit, dispatch, rootGetters }: ActionArguments, data: GetUmfrageInfoRequest) {
    const status = rootGetters.status;
    status.getBewertungAntwortLoading = true;
    status.bewertungAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.GetUmfrageInfo(data)
      .then((res) => {
        const data: UmfrageInfo = res.data;
        commit(MutationTypes.SetUmfrageInfo, data);
      })
      .catch((error) => {
        const err = error.response.data as ErrorResponseData;
        console.log(err.title);
        status.bewertungAntwortErrorMsg = err.title;
      })
      .finally(() => {
        status.getBewertungAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateBewertungAntwort]({ commit, dispatch, rootGetters }: ActionArguments, data: BewertungAntwort) {
    const status = rootGetters.status;
    status.updateBewertungAntwortLoading = true;
    status.bewertungAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.UpdateBewertungAntwort(data)
      .then((res) => {
        const data: BewertungAntwort = res.data;
        commit(MutationTypes.AddOrUpdateBewertungantwort, data);
      })
      .catch((error) => {
        status.bewertungAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.updateBewertungAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateBewertungAntwortRange]({ commit, dispatch, rootGetters }: ActionArguments, data: Array<BewertungAntwort>) {
    const status = rootGetters.status;
    status.updateBewertungAntwortLoading = true;
    status.bewertungAntwortErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.UpdateBewertungAntwortRange(data)
      .then((res) => {
        const data: Array<BewertungAntwort> = res.data;
        commit(MutationTypes.AddOrUpdateBewertungantwortRange, data);
      })
      .catch((error) => {
        status.bewertungAntwortErrorMsg = error.response;
      })
      .finally(() => {
        status.updateBewertungAntwortLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
};
